import React, { useCallback, useMemo, useState } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Position,
  MarkerType,
  ConnectionLineType,
  Handle,
  EdgeProps,
  getBezierPath,
  BaseEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { Box, Typography, useTheme, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CardView from '../../../components/Redesing/card-view';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { removeAssignment } from '../../../api/hardware';
import { history, toTitleCase } from '../../../utils';
import { IHardware } from '../../../ts/interface/hardware.interface';

// CSS for edge delete button
const edgeButtonStyles = `
  .edgebutton-foreignobject {
    overflow: visible !important;
    pointer-events: all !important;
  }
  .edgebutton-foreignobject div {
    pointer-events: all !important;
  }
`;

interface AssignmentsGraphProps {
  hardware: IHardware;
}

interface AssignmentData {
  id?: string;
  gid?: string;
  parent_id?: string;
  child_id?: string;
  number?: number;
  label?: string | null;
  mac?: string | null;
  imei?: string | null;
  vendor_id?: string | null;
  vendor_serial?: string | null;
  parent_hardware_type_name?: string;
  child_hardware_type_name?: string;
  hardware_type_name?: string;
  primary_identifier?: string;
  removed_on?: string | null;
  child?: boolean;
}

const nodeWidth = 200;
const nodeHeight = 80;

const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = 'TB'
): { nodes: Node[]; edges: Edge[] } => {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction, nodesep: 50, ranksep: 80 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Custom edge with delete button
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}: EdgeProps) => {
  const theme = useTheme();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (data?.onDelete) {
      data.onDelete(data.parentId, data.childId);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <foreignObject
        width={24}
        height={24}
        x={labelX - 12}
        y={labelY - 12}
        className="edgebutton-foreignobject"
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.grey[300]}`,
            cursor: 'pointer',
            pointerEvents: 'all',
          }}
          onClick={onDeleteClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.palette.error.light;
            e.currentTarget.style.borderColor = theme.palette.error.main;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = theme.palette.background.paper;
            e.currentTarget.style.borderColor = theme.palette.grey[300];
          }}
        >
          <DeleteIcon sx={{ fontSize: 14, color: theme.palette.grey[500] }} />
        </div>
      </foreignObject>
    </>
  );
};

const CustomNode = ({
  data,
}: {
  data: {
    label: string;
    typeName: string;
    isCurrent: boolean;
    hardwareId?: string;
    onAddAssignment?: () => void;
  };
}) => {
  const theme = useTheme();

  const handleAddClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (data.onAddAssignment) {
      data.onAddAssignment();
    }
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        id="target"
        style={{ visibility: 'hidden' }}
      />
      <Box
        sx={{
          position: 'relative',
          padding: '12px 20px',
          borderRadius: '12px',
          border: `2px solid ${
            data.isCurrent ? theme.palette.primary.main : theme.palette.grey[300]
          }`,
          backgroundColor: data.isCurrent
            ? theme.palette.primary.main
            : theme.palette.background.paper,
          cursor: data.isCurrent ? 'default' : 'pointer',
          width: nodeWidth,
          height: nodeHeight,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'all 0.2s ease',
          boxShadow: data.isCurrent ? theme.shadows[2] : 'none',
          '&:hover': data.isCurrent
            ? {}
            : {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.shadows[4],
              },
        }}
      >
        {data.isCurrent && (
          <IconButton
            size="small"
            onClick={handleAddClick}
            sx={{
              position: 'absolute',
              bottom: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              width: 24,
              height: 24,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.grey[300]}`,
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <Typography
          variant="body2"
          sx={{
            color: data.isCurrent
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            fontWeight: 500,
          }}
        >
          {data.typeName}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: data.isCurrent
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            fontWeight: 600,
            fontSize: data.label.length > 10 ? '0.95rem' : '1.1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {data.label}
        </Typography>
      </Box>
      <Handle
        type="source"
        position={Position.Bottom}
        id="source"
        style={{ visibility: 'hidden' }}
      />
    </>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const HardwareAssignmentsGraph = ({ hardware }: AssignmentsGraphProps) => {
  const theme = useTheme();
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    parentId: string;
    childId: string;
  }>({ open: false, parentId: '', childId: '' });

  const handleDeleteEdge = useCallback((parentId: string, childId: string) => {
    setConfirmModal({ open: true, parentId, childId });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    CustomAsyncToast({
      promise: () =>
        removeAssignment({
          hardware_parent_id: confirmModal.parentId,
          hardware_child_id: confirmModal.childId,
        }),
      successMessage: () => 'Assignment removed successfully!',
      errorMessage: "Oops! We couldn't remove the assignment. Please try again.",
      loadingMessage: 'Removing Assignment...',
    }).then((success) => {
      if (success) {
        setConfirmModal({ open: false, parentId: '', childId: '' });
      }
    });
  }, [confirmModal.parentId, confirmModal.childId]);

  const handleCancelDelete = useCallback(() => {
    setConfirmModal({ open: false, parentId: '', childId: '' });
  }, []);

  const handleAddAssignment = useCallback(() => {
    history.push(`/hardware/${hardware?.gid || hardware?.id}/add-assignment`);
  }, [hardware?.gid, hardware?.id]);

  const getDisplayValue = useCallback(
    (item: AssignmentData, isChild: boolean): string => {
      // Use primary_identifier to determine which value to display (same as Assignments table)
      const identifier = item.primary_identifier;

      if (identifier === 'mac' && item.mac) {
        return item.mac;
      }
      if (identifier === 'imei' && item.imei) {
        return item.imei;
      }
      if (identifier === 'vendor_id' && item.vendor_id) {
        return item.vendor_id;
      }
      if (identifier === 'vendor_serial' && item.vendor_serial) {
        return item.vendor_serial;
      }
      if (identifier === 'number' && item.number !== undefined && item.number !== null) {
        return String(item.number);
      }

      // Fallback: try each identifier type
      if (item.number !== undefined && item.number !== null && item.number !== 0) {
        return String(item.number);
      }
      if (item.label) {
        return item.label;
      }
      if (item.mac) {
        return item.mac;
      }
      if (item.imei) {
        return item.imei;
      }
      if (item.vendor_id) {
        return item.vendor_id;
      }
      if (item.vendor_serial) {
        return item.vendor_serial;
      }

      return isChild ? item.child_id || '' : item.parent_id || '';
    },
    []
  );

  const getHardwareDisplayValue = useCallback((): string => {
    if (
      hardware.number !== undefined &&
      hardware.number !== null &&
      hardware.number !== 0
    ) {
      return String(hardware.number);
    }
    if (hardware.label) {
      return hardware.label;
    }
    if (hardware.imei) {
      return hardware.imei;
    }
    if (hardware.mac) {
      return hardware.mac;
    }
    return hardware.gid || hardware.id || '';
  }, [hardware]);

  // Filter active assignments (not removed)
  const parents: AssignmentData[] = useMemo(
    () => (hardware?.parents || []).filter((p: AssignmentData) => !p.removed_on),
    [hardware?.parents]
  );
  const children: AssignmentData[] = useMemo(
    () => (hardware?.children || []).filter((c: AssignmentData) => !c.removed_on),
    [hardware?.children]
  );

  // Create a stable key based on assignment IDs to prevent unnecessary re-renders
  const graphKey = useMemo(() => {
    const parentIds = parents.map((p) => p.parent_id || p.gid).join(',');
    const childIds = children.map((c) => c.child_id || c.gid).join(',');
    return `${hardware?.gid || hardware?.id}-${parentIds}-${childIds}`;
  }, [hardware?.gid, hardware?.id, parents, children]);

  const { layoutedNodes, layoutedEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Add current hardware node
    nodes.push({
      id: 'current',
      type: 'custom',
      position: { x: 0, y: 0 },
      data: {
        label: getHardwareDisplayValue(),
        typeName: toTitleCase(
          hardware.hardware_type_name || hardware.type_name || 'Hardware'
        ),
        isCurrent: true,
        onAddAssignment: handleAddAssignment,
      },
    });

    // Add parent nodes (above current)
    parents.forEach((parent, index) => {
      const parentId = parent.parent_id || parent.gid || `parent-${index}`;

      nodes.push({
        id: `parent-${parentId}`,
        type: 'custom',
        position: { x: 0, y: 0 },
        data: {
          label: getDisplayValue(parent, false),
          typeName: toTitleCase(parent.parent_hardware_type_name || 'Parent'),
          isCurrent: false,
          hardwareId: parentId,
        },
      });

      // Edge from parent to current (parent -> current)
      // For parent assignments, the current hardware is the child
      const currentHardwareId = hardware.gid || hardware.id;
      edges.push({
        id: `edge-parent-${parentId}`,
        source: `parent-${parentId}`,
        sourceHandle: 'source',
        target: 'current',
        targetHandle: 'target',
        type: 'custom',
        animated: false,
        style: { stroke: theme.palette.grey[400], strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: theme.palette.grey[400],
          width: 20,
          height: 20,
        },
        data: {
          parentId: parentId,
          childId: currentHardwareId,
          onDelete: handleDeleteEdge,
        },
      });
    });

    // Add child nodes (below current)
    children.forEach((child, index) => {
      const childId = child.child_id || child.gid || `child-${index}`;

      nodes.push({
        id: `child-${childId}`,
        type: 'custom',
        position: { x: 0, y: 0 },
        data: {
          label: getDisplayValue(child, true),
          typeName: toTitleCase(child.child_hardware_type_name || 'Child'),
          isCurrent: false,
          hardwareId: childId,
        },
      });

      // Edge from current to child (current -> child)
      // For child assignments, the current hardware is the parent
      const currentHardwareId = hardware.gid || hardware.id;
      edges.push({
        id: `edge-child-${childId}`,
        source: 'current',
        sourceHandle: 'source',
        target: `child-${childId}`,
        targetHandle: 'target',
        type: 'custom',
        animated: false,
        style: { stroke: theme.palette.grey[400], strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: theme.palette.grey[400],
          width: 20,
          height: 20,
        },
        data: {
          parentId: currentHardwareId,
          childId: childId,
          onDelete: handleDeleteEdge,
        },
      });
    });

    // Apply dagre layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      'TB' // Top to Bottom
    );

    return { layoutedNodes, layoutedEdges };
  }, [
    parents,
    children,
    getDisplayValue,
    getHardwareDisplayValue,
    handleDeleteEdge,
    handleAddAssignment,
    theme.palette.grey,
    hardware.gid,
    hardware.id,
    hardware.hardware_type_name,
    hardware.type_name,
  ]);

  const handleNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const hardwareId = node.data?.hardwareId;
    if (hardwareId && !node.data?.isCurrent) {
      history.push(`/hardware/${hardwareId}`);
    }
  }, []);

  // Calculate dynamic height based on the number of levels
  const levels = (parents.length > 0 ? 1 : 0) + 1 + (children.length > 0 ? 1 : 0);
  const graphHeight = Math.max(250, levels * (nodeHeight + 80) + 50);

  return (
    <>
      <style>{edgeButtonStyles}</style>
      <CardView
        title="Assignments Graph"
        content={
          <Box sx={{ height: graphHeight, width: '100%' }}>
            <ReactFlow
              key={graphKey}
              defaultNodes={layoutedNodes}
              defaultEdges={layoutedEdges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              connectionLineType={ConnectionLineType.SmoothStep}
              onNodeClick={handleNodeClick}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              panOnScroll={false}
              panOnDrag={false}
              preventScrolling={false}
              proOptions={{ hideAttribution: true }}
            />
          </Box>
        }
      />
      <MovaticCustomModal
        open={confirmModal.open}
        onClose={handleCancelDelete}
        title="Remove Assignment"
        content={<div>Are you sure you want to remove this assignment?</div>}
        customStyles={{
          title: { p: 2 },
          action: { p: 2 },
          content: { p: 2 },
        }}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: handleCancelDelete,
                startIcon: <CloseIcon />,
              },
              {
                label: 'Remove',
                onClick: handleConfirmDelete,
                startIcon: <CheckIcon />,
              },
            ]}
          />
        }
      />
    </>
  );
};

export default HardwareAssignmentsGraph;
